import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

const ThemeTransition = ({ children, theme, animationType = 'corner' }) => {
  const location = useLocation();
  const [displayTheme, setDisplayTheme] = React.useState(theme);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [direction, setDirection] = React.useState('left');

  React.useEffect(() => {
    if (theme !== displayTheme) {
      // Randomly choose direction for variety, or you can set it based on theme
      const directions = ['left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right'];
      setDirection(directions[Math.floor(Math.random() * directions.length)]);
      
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayTheme(theme);
        setTimeout(() => setIsTransitioning(false), 500);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [theme, displayTheme]);

  // Left to Right animation
  const leftToRightVariants = {
    initial: { 
      opacity: 0,
      x: -100,
      scale: 0.95
    },
    in: { 
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1], // cubic-bezier for smooth motion
        staggerChildren: 0.05
      }
    },
    out: { 
      opacity: 0,
      x: 100,
      scale: 0.95,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  // Right to Left animation
  const rightToLeftVariants = {
    initial: { 
      opacity: 0,
      x: 100,
      scale: 0.95
    },
    in: { 
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
        staggerChildren: 0.05
      }
    },
    out: { 
      opacity: 0,
      x: -100,
      scale: 0.95,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  // Top-Left to Bottom-Right animation (corner to corner)
  const topLeftToBottomRightVariants = {
    initial: { 
      opacity: 0,
      x: -100,
      y: -100,
      scale: 0.8,
      rotate: -5
    },
    in: { 
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1], // spring-like easing
        staggerChildren: 0.05
      }
    },
    out: { 
      opacity: 0,
      x: 100,
      y: 100,
      scale: 0.8,
      rotate: 5,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Top-Right to Bottom-Left animation
  const topRightToBottomLeftVariants = {
    initial: { 
      opacity: 0,
      x: 100,
      y: -100,
      scale: 0.8,
      rotate: 5
    },
    in: { 
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.05
      }
    },
    out: { 
      opacity: 0,
      x: -100,
      y: 100,
      scale: 0.8,
      rotate: -5,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Bottom-Left to Top-Right animation
  const bottomLeftToTopRightVariants = {
    initial: { 
      opacity: 0,
      x: -100,
      y: 100,
      scale: 0.8,
      rotate: 5
    },
    in: { 
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.05
      }
    },
    out: { 
      opacity: 0,
      x: 100,
      y: -100,
      scale: 0.8,
      rotate: -5,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Bottom-Right to Top-Left animation
  const bottomRightToTopLeftVariants = {
    initial: { 
      opacity: 0,
      x: 100,
      y: 100,
      scale: 0.8,
      rotate: -5
    },
    in: { 
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.05
      }
    },
    out: { 
      opacity: 0,
      x: -100,
      y: -100,
      scale: 0.8,
      rotate: 5,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Select variants based on direction or animationType
  const getVariants = () => {
    if (animationType === 'left-right') {
      return direction === 'left' ? leftToRightVariants : rightToLeftVariants;
    } else if (animationType === 'corner') {
      switch(direction) {
        case 'top-left':
          return topLeftToBottomRightVariants;
        case 'top-right':
          return topRightToBottomLeftVariants;
        case 'bottom-left':
          return bottomLeftToTopRightVariants;
        case 'bottom-right':
          return bottomRightToTopLeftVariants;
        default:
          return topLeftToBottomRightVariants;
      }
    }
    return leftToRightVariants; // default
  };

  // CSS animation classes for additional effects
  const getAnimationClass = () => {
    if (!isTransitioning) return '';
    
    switch(direction) {
      case 'left':
        return 'animate-slide-from-left';
      case 'right':
        return 'animate-slide-from-right';
      case 'top-left':
        return 'animate-slide-from-top-left';
      case 'top-right':
        return 'animate-slide-from-top-right';
      case 'bottom-left':
        return 'animate-slide-from-bottom-left';
      case 'bottom-right':
        return 'animate-slide-from-bottom-right';
      default:
        return 'animate-slide-from-left';
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname + displayTheme + direction}
        initial="initial"
        animate="in"
        exit="out"
        variants={getVariants()}
        className={`${getAnimationClass()} w-full`}
        style={{
          position: 'relative',
          width: '100%'
        }}
      >
        {/* Optional overlay effect for corner transitions */}
        {isTransitioning && animationType === 'corner' && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.1, scale: 1.5 }}
            exit={{ opacity: 0, scale: 2 }}
            transition={{ duration: 0.8 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: theme === 'dark' 
                ? 'radial-gradient(circle at var(--x, 50%) var(--y, 50%), rgba(255,255,255,0.1) 0%, transparent 70%)'
                : 'radial-gradient(circle at var(--x, 50%) var(--y, 50%), rgba(0,0,0,0.1) 0%, transparent 70%)',
              pointerEvents: 'none',
              zIndex: 9999
            }}
          />
        )}
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default ThemeTransition;